import {defineStore} from 'pinia'
import type {Agence, Agent, Direction, Domaine, Equipe, Section, Paged, PageMeta} from '../types/org'
import {
    keyDomaine,
    keyAgence,
    keyEquipe,
    keySection,
    keyDirection
} from '../types/org'

type Id = string

interface OrgState {
    isEditing: boolean
    editEnteredAtMs: number | null

    directions: Map<Id, Direction>
    domaines: Map<Id, Domaine>
    agences: Map<Id, Agence>
    equipes: Map<Id, Equipe>
    sections: Map<Id, Section>
    agents: Map<Id, Agent>

    byDir_domaines: Map<string, Set<string>>
    byDirDom_agences: Map<string, Set<string>>
    byDirDomAge_equipes: Map<string, Set<string>>
    byEquipe_sections: Map<string, Set<string>>
    bySection_agents: Map<string, Set<string>>

    meta: {
        directions?: PageMeta
        domaines?: PageMeta
        agences?: PageMeta
        equipes?: PageMeta
        sections?: PageMeta
        agents?: PageMeta
    }
}

export const useOrgStore = defineStore('org', {
    state: (): OrgState => ({
        isEditing: false,
        editEnteredAtMs: null,

        directions: new Map(),
        domaines: new Map(),
        agences: new Map(),
        equipes: new Map(),
        sections: new Map(),
        agents: new Map(),

        byDir_domaines: new Map(),
        byDirDom_agences: new Map(),
        byDirDomAge_equipes: new Map(),
        byEquipe_sections: new Map(),
        bySection_agents: new Map(),

        meta: {}
    }),

    getters: {
        listDirections: (s): Direction[] =>
            [...s.directions.values()].sort((a, b) => a.dirLib.localeCompare(b.dirLib)),

        listDomainesByDir: (s) => (dirLib: string): Domaine[] => {
            const set = s.byDir_domaines.get(dirLib)
            if (!set) return []
            return [...set]
                .map(k => s.domaines.get(k)!)
                .filter(Boolean)
                .sort((a, b) => (a.domLib ?? '').localeCompare(b.domLib ?? ''))
        },

        listAgencesByDirDom: (s) => (dirLib: string, domLib: string): Agence[] => {
            const parentKey = keyDomaine(dirLib, domLib)
            const set = s.byDirDom_agences.get(parentKey)
            if (!set) return []
            return [...set]
                .map(k => s.agences.get(k)!)
                .filter(Boolean)
                .sort((a, b) => (a.ageLib ?? '').localeCompare(b.ageLib ?? ''))
        },

        listEquipesByAgence: (s) => (dirLib: string, domLib: string, ageLib: string): Equipe[] => {
            const parentKey = keyAgence(dirLib, domLib, ageLib)
            const set = s.byDirDomAge_equipes.get(parentKey)
            if (!set) return []
            return [...set]
                .map(k => s.equipes.get(k)!)
                .filter(Boolean)
                .sort((a, b) => a.equLib.localeCompare(b.equLib))
        },

        listSectionsByEquipe: (s) => (dirLib: string, domLib: string, ageLib: string, equLib: string): Section[] => {
            const ek = keyEquipe(dirLib, domLib, ageLib, equLib)
            const set = s.byEquipe_sections.get(ek)
            if (!set) return []
            return [...set]
                .map(k => s.sections.get(k)!)
                .filter(Boolean)
                .sort((a, b) => a.secLib.localeCompare(b.secLib))
        },

        listAgentsBySectionKey: (s) => (sectionKey: string): Agent[] => {
            const set = s.bySection_agents.get(sectionKey)
            if (!set) return []
            return [...set].map(id => s.agents.get(id)!).filter(Boolean)
                .sort((a, b) =>
                    `${a.lastName ?? ''}${a.firstName ?? ''}`.localeCompare(`${b.lastName ?? ''}${b.firstName ?? ''}`)
                )
        },

        getEquipeKey: () => (e: Pick<Equipe, 'dirLib' | 'domLib' | 'ageLib' | 'equLib'>) =>
            keyEquipe(e.dirLib, e.domLib, e.ageLib, e.equLib),

        getSectionKey: () => (dirLib: string, domLib: string, ageLib: string, equLib: string, secLib: string) =>
            keySection(dirLib, domLib, ageLib, equLib, secLib),

        treeByDirection: (s) => (dirLib: string) => {
            const direction = s.directions.get(keyDirection(dirLib))
            if (!direction) return null

            const domSet = s.byDir_domaines.get(keyDirection(dirLib))
            const domaines = domSet
                ? [...domSet].map((domainKey) => s.domaines.get(domainKey)!).filter(Boolean)
                : []

            return {
                ...direction,
                domaines: domaines.map((d: Domaine) => {
                    const agenceSet = s.byDirDom_agences.get(keyDomaine(dirLib, d.domLib ?? ''))
                    const agences = agenceSet
                        ? [...agenceSet].map((agenceKey) => s.agences.get(agenceKey)!).filter(Boolean)
                        : []
                    return {
                        ...d,
                        agences: agences.map((a: Agence) => {
                            const equipeSet = s.byDirDomAge_equipes.get(keyAgence(dirLib, d.domLib ?? '', a.ageLib ?? ''))
                            const equipes = equipeSet
                                ? [...equipeSet].map((equipeKey) => s.equipes.get(equipeKey)!).filter(Boolean)
                                : []
                            return {
                                ...a,
                                equipes: equipes.map((eq: Equipe) => {
                                    const sectionSet = s.byEquipe_sections.get(
                                        keyEquipe(dirLib, d.domLib ?? '', a.ageLib ?? '', eq.equLib),
                                    )
                                    const sections = sectionSet
                                        ? [...sectionSet].map((sectionKey) => s.sections.get(sectionKey)!).filter(Boolean)
                                        : []
                                    return {
                                        ...eq,
                                        sections: sections.map((sec: Section) => {
                                            const secKey = keySection(
                                                dirLib,
                                                d.domLib ?? '',
                                                a.ageLib ?? '',
                                                eq.equLib,
                                                sec.secLib,
                                            )
                                            const agentSet = s.bySection_agents.get(secKey)
                                            const agents = agentSet
                                                ? [...agentSet]
                                                    .map((agentId) => s.agents.get(agentId)!)
                                                    .filter(Boolean)
                                                : []
                                            return {...sec, agents}
                                        }),
                                    }
                                }),
                            }
                        }),
                    }
                }),
            }
        }
    },

    actions: {
        reset() {
            this.$reset()
        },

        _indexDomaine(dirLib: string, domLib?: string) {
            if (!domLib) return
            const dKey = keyDirection(dirLib)
            const domKey = keyDomaine(dirLib, domLib)
            if (!this.byDir_domaines.has(dKey)) this.byDir_domaines.set(dKey, new Set())
            this.byDir_domaines.get(dKey)!.add(domKey)
        },
        _indexAgence(dirLib?: string, domLib?: string, ageLib?: string) {
            if (!dirLib || !domLib || !ageLib) return
            const parentKey = keyDomaine(dirLib, domLib)
            const agKey = keyAgence(dirLib, domLib, ageLib)
            if (!this.byDirDom_agences.has(parentKey)) this.byDirDom_agences.set(parentKey, new Set())
            this.byDirDom_agences.get(parentKey)!.add(agKey)
        },
        _indexEquipe(e: Equipe) {
            const agKey = keyAgence(e.dirLib, e.domLib, e.ageLib)
            const eqKey = keyEquipe(e.dirLib, e.domLib, e.ageLib, e.equLib)
            if (!this.byDirDomAge_equipes.has(agKey)) this.byDirDomAge_equipes.set(agKey, new Set())
            this.byDirDomAge_equipes.get(agKey)!.add(eqKey)
            return eqKey
        },
        _indexSection(dirLib: string, domLib: string, ageLib: string, equLib: string, sec: Section) {
            const eqKey = keyEquipe(dirLib, domLib, ageLib, equLib)
            const secKey = keySection(dirLib, domLib, ageLib, equLib, sec.secLib)
            if (!this.byEquipe_sections.has(eqKey)) this.byEquipe_sections.set(eqKey, new Set())
            this.byEquipe_sections.get(eqKey)!.add(secKey)
            return secKey
        },
        _indexSectionAgents(secKey: string, agents?: Agent[]) {
            if (!agents?.length) return
            if (!this.bySection_agents.has(secKey)) this.bySection_agents.set(secKey, new Set())
            const set = this.bySection_agents.get(secKey)!
            for (const a of agents) {
                this.agents.set(a.id, a)
                set.add(a.id)
            }
        },

        upsertDirection(d: Direction) {
            this.directions.set(keyDirection(d.dirLib), d)
        },
        upsertDomaine(dom: Domaine) {
            this.domaines.set(keyDomaine(dom.dirLib ?? '', dom.domLib ?? ''), dom)
            if (dom.dirLib && dom.domLib) this._indexDomaine(dom.dirLib, dom.domLib)
        },
        upsertAgence(a: Agence) {
            this.agences.set(keyAgence(a.dirLib ?? '', a.domLib ?? '', a.ageLib ?? ''), a)
            this._indexAgence(a.dirLib, a.domLib, a.ageLib)
        },
        upsertEquipe(e: Equipe) {
            const eqKey = this._indexEquipe(e)
            this.equipes.set(eqKey, e)
            if (e.sections?.length) {
                for (const sec of e.sections) {
                    const secKey = this._indexSection(e.dirLib, e.domLib, e.ageLib, e.equLib, sec)
                    this.sections.set(secKey, {...sec})
                    this._indexSectionAgents(secKey, sec.agents)
                }
            }
        },
        upsertSection(dirLib: string, domLib: string, ageLib: string, equLib: string, sec: Section) {
            const secKey = this._indexSection(dirLib, domLib, ageLib, equLib, sec)
            this.sections.set(secKey, {...sec})
            this._indexSectionAgents(secKey, sec.agents)
        },
        upsertAgent(agent: Agent) {
            this.agents.set(agent.id, agent)
        },

        removeDirection(dirLib: string) {
            const dKey = keyDirection(dirLib)
            const dSet = this.byDir_domaines.get(dKey)
            if (dSet) {
                for (const domKey of dSet) {
                    const [d, dom] = domKey.split('::')
                    if (d && dom) {
                        this.removeDomaine(d, dom)
                    }
                }
                this.byDir_domaines.delete(dKey)
            }
            this.directions.delete(dKey)
        },
        removeDomaine(dirLib: string, domLib: string) {
            const domKey = keyDomaine(dirLib, domLib)
            const aSet = this.byDirDom_agences.get(domKey)
            if (aSet) {
                for (const agKey of aSet) {
                    const [d, dom, age] = agKey.split('::')
                    if (d && dom && age) {
                        this.removeAgence(d, dom, age)
                    }
                }
                this.byDirDom_agences.delete(domKey)
            }
            this.domaines.delete(domKey)
            const dSet = this.byDir_domaines.get(dirLib)
            if (dSet) dSet.delete(domKey)
        },
        removeAgence(dirLib: string, domLib: string, ageLib: string) {
            const agKey = keyAgence(dirLib, domLib, ageLib)
            const eSet = this.byDirDomAge_equipes.get(agKey)
            if (eSet) {
                for (const eqKey of eSet) {
                    const [dir, dom, age, equ] = eqKey.split('::')
                    if (dir && dom && age && equ) {
                        this.removeEquipe(dir, dom, age, equ)
                    }
                }
                this.byDirDomAge_equipes.delete(agKey)
            }
            this.agences.delete(agKey)
            const pSet = this.byDirDom_agences.get(keyDomaine(dirLib, domLib))
            if (pSet) pSet.delete(agKey)
        },
        removeEquipe(dirLib: string, domLib: string, ageLib: string, equLib: string) {
            const eqKey = keyEquipe(dirLib, domLib, ageLib, equLib)
            const sSet = this.byEquipe_sections.get(eqKey)
            if (sSet) {
                for (const secKey of sSet) {
                    this.removeSectionByKey(secKey)
                }
                this.byEquipe_sections.delete(eqKey)
            }
            this.equipes.delete(eqKey)
            const pSet = this.byDirDomAge_equipes.get(keyAgence(dirLib, domLib, ageLib))
            if (pSet) pSet.delete(eqKey)
        },
        removeSectionByKey(secKey: string) {
            const aSet = this.bySection_agents.get(secKey)
            if (aSet) {
                this.bySection_agents.delete(secKey)
            }
            this.sections.delete(secKey)
            const parts = secKey.split('::')
            const eqKey = parts.slice(0, 4).join('::')
            const sSet = this.byEquipe_sections.get(eqKey)
            if (sSet) sSet.delete(secKey)
        },

        loadDirections(payload: Direction[] | Paged<Direction>) {
            const arr = Array.isArray(payload) ? payload : payload.data
            for (const d of arr) this.upsertDirection(d)
            if (!Array.isArray(payload)) this.meta.directions = payload.meta
        },
        loadDomaines(payload: Domaine[] | Paged<Domaine>) {
            const arr = Array.isArray(payload) ? payload : payload.data
            for (const dom of arr) this.upsertDomaine(dom)
            if (!Array.isArray(payload)) this.meta.domaines = payload.meta
        },
        loadAgences(payload: Agence[] | Paged<Agence>) {
            const arr = Array.isArray(payload) ? payload : payload.data
            for (const a of arr) this.upsertAgence(a)
            if (!Array.isArray(payload)) this.meta.agences = payload.meta
        },
        loadEquipes(payload: Equipe[] | Paged<Equipe>) {
            const arr = Array.isArray(payload) ? payload : payload.data
            for (const e of arr) this.upsertEquipe(e)
            if (!Array.isArray(payload)) this.meta.equipes = payload.meta
        },
        loadSections(
            dirLib: string,
            domLib: string,
            ageLib: string,
            equLib: string,
            payload: Section[] | Paged<Section>
        ) {
            const arr = Array.isArray(payload) ? payload : payload.data
            for (const s of arr) this.upsertSection(dirLib, domLib, ageLib, equLib, s)
            if (!Array.isArray(payload)) this.meta.sections = payload.meta
        },
        loadAgents(payload: Agent[] | Paged<Agent>) {
            const arr = Array.isArray(payload) ? payload : payload.data
            for (const ag of arr) this.upsertAgent(ag)
            if (!Array.isArray(payload)) this.meta.agents = payload.meta
        },

        attachAgentsToSectionKey(sectionKey: string, agentIds: string[]) {
            if (!this.bySection_agents.has(sectionKey)) this.bySection_agents.set(sectionKey, new Set())
            const set = this.bySection_agents.get(sectionKey)!
            for (const id of agentIds) set.add(id)
        },
        detachAgentsFromSectionKey(sectionKey: string, agentIds: string[]) {
            const set = this.bySection_agents.get(sectionKey)
            if (!set) return
            for (const id of agentIds) set.delete(id)
        },

        setIsEditing(value: boolean) {
            this.isEditing = value
            if (value) {
                this.editEnteredAtMs = Date.now()
            }
        }

    }
})